import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MESSAGE_KIND,
  buildCharacterizationLoadingMessage,
  nextCharacterizationLoadingTurn,
  refreshCharacterizationLoadingProgress,
  splitProgressSentenceForOdometer,
} from "./characterizationLoadingMessage";

const CHAR_MS = 44;
const PAUSE_TEXT_MS = 4500;
const PAUSE_PROGRESS_MS = 6000;
const ODOMETER_DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

const TYPEWRITER_STATUS = {
  TYPING: "typing",
  HOLDING: "holding",
};

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches),
  );

  useEffect(() => {
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!media) return undefined;
    const onChange = () => setReduced(media.matches);
    media.addEventListener?.("change", onChange);
    return () => media.removeEventListener?.("change", onChange);
  }, []);

  return reduced;
};

const RollingDigits = ({ value, reducedMotion }) => {
  const digits = String(Math.max(0, value ?? 0)).split("");
  const [canAnimate, setCanAnimate] = useState(false);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => setCanAnimate(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  return (
    <span className="dashboard-char-loading-odometer" aria-hidden="true">
      {digits.map((digit, index) => (
        <span
          className="dashboard-char-loading-odometer-digit"
          key={`${digits.length}-${index}`}
        >
          <span
            className={
              reducedMotion || !canAnimate
                ? "dashboard-char-loading-odometer-strip is-static"
                : "dashboard-char-loading-odometer-strip"
            }
            style={{
              transform: `translate3d(0, calc(-${Number(digit)} * var(--odometer-digit-height)), 0)`,
            }}
          >
            {ODOMETER_DIGITS.map((n) => (
              <span className="dashboard-char-loading-odometer-glyph" key={n}>
                {n}
              </span>
            ))}
          </span>
        </span>
      ))}
    </span>
  );
};

const useTypewriter = (text, { reducedMotion, enabled, charMs = CHAR_MS }) => {
  const [displayed, setDisplayed] = useState("");
  const [status, setStatus] = useState(TYPEWRITER_STATUS.TYPING);

  useLayoutEffect(() => {
    if (!enabled) {
      setDisplayed(text || "");
      setStatus(TYPEWRITER_STATUS.HOLDING);
      return undefined;
    }

    if (!text) {
      setDisplayed("");
      setStatus(TYPEWRITER_STATUS.HOLDING);
      return undefined;
    }

    if (reducedMotion) {
      setDisplayed(text);
      setStatus(TYPEWRITER_STATUS.HOLDING);
      return undefined;
    }

    let index = 0;
    setDisplayed("");
    setStatus(TYPEWRITER_STATUS.TYPING);

    const typeId = window.setInterval(() => {
      index += 1;
      setDisplayed(text.slice(0, index));
      if (index >= text.length) {
        window.clearInterval(typeId);
        setStatus(TYPEWRITER_STATUS.HOLDING);
      }
    }, charMs);

    return () => window.clearInterval(typeId);
  }, [text, reducedMotion, enabled, charMs]);

  return { displayed, status };
};

const ProgressSentence = ({ text, value, reducedMotion }) => {
  const parts = useMemo(
    () => splitProgressSentenceForOdometer(text, value),
    [text, value],
  );

  return (
    <p className="dashboard-char-loading-text" aria-hidden="true">
      {parts.map((part, index) =>
        part.type === "number" ? (
          <RollingDigits
            key={`n-${index}`}
            value={part.value}
            reducedMotion={reducedMotion}
          />
        ) : (
          <span key={`t-${index}`}>{part.value}</span>
        ),
      )}
    </p>
  );
};

export const CharacterizationLoadingStatus = ({
  count,
  loaded,
  periodLabel,
}) => {
  const { t } = useTranslation();
  const reducedMotion = usePrefersReducedMotion();
  const startingMessage = t("dashboard.characterizationLoadingStarting");
  const liveMessage = buildCharacterizationLoadingMessage({
    count,
    loaded,
    periodLabel,
    t,
  });

  const [frame, setFrame] = useState({
    kind: MESSAGE_KIND.TEXT,
    text: startingMessage,
  });
  const [pauseMs, setPauseMs] = useState(PAUSE_TEXT_MS);

  const lastFactualRef = useRef(startingMessage);
  const lastFunKeyRef = useRef(null);
  const lastWasFunRef = useRef(false);
  const funBagRef = useRef([]);
  const progressKeyRef = useRef(null);
  const progressBagRef = useRef([]);
  const frameRef = useRef(frame);
  const liveMessageRef = useRef(liveMessage);
  const loadedRef = useRef(loaded);
  const countRef = useRef(count);

  frameRef.current = frame;
  liveMessageRef.current = liveMessage;
  loadedRef.current = loaded;
  countRef.current = count;

  const isProgress = frame.kind === MESSAGE_KIND.PROGRESS;
  const { displayed, status } = useTypewriter(frame.text, {
    reducedMotion,
    enabled: !isProgress,
  });

  const applyTurn = (nextTurn) => {
    if (!nextTurn) return false;
    const current = frameRef.current;
    const sameProgress =
      nextTurn.kind === MESSAGE_KIND.PROGRESS &&
      current.kind === MESSAGE_KIND.PROGRESS &&
      nextTurn.templateKey === current.templateKey &&
      nextTurn.value === current.value;
    if (sameProgress) return false;
    if (
      nextTurn.kind === MESSAGE_KIND.TEXT &&
      current.kind === MESSAGE_KIND.TEXT &&
      nextTurn.text === current.text
    ) {
      return false;
    }

    lastFactualRef.current = nextTurn.lastFactual;
    lastWasFunRef.current = nextTurn.lastWasFun;
    lastFunKeyRef.current = nextTurn.lastFunKey;
    funBagRef.current = nextTurn.funBag ?? funBagRef.current;
    progressKeyRef.current = nextTurn.progressKey;
    if (nextTurn.progressBag) {
      progressBagRef.current = nextTurn.progressBag;
    }
    setPauseMs(
      nextTurn.kind === MESSAGE_KIND.PROGRESS
        ? PAUSE_PROGRESS_MS
        : PAUSE_TEXT_MS,
    );
    setFrame({
      kind: nextTurn.kind,
      text: nextTurn.text,
      templateKey: nextTurn.templateKey,
      value: nextTurn.value,
      loaded: nextTurn.loaded,
      total: nextTurn.total,
    });
    return true;
  };

  useEffect(() => {
    const ready = isProgress || status === TYPEWRITER_STATUS.HOLDING;
    if (!ready) return undefined;

    const holdId = window.setTimeout(() => {
      const nextTurn = nextCharacterizationLoadingTurn({
        liveMessage: liveMessageRef.current,
        loaded: loadedRef.current,
        total: countRef.current,
        lastFactual: lastFactualRef.current,
        lastWasFun: lastWasFunRef.current,
        lastFunKey: lastFunKeyRef.current,
        funBag: funBagRef.current,
        progressKey: progressKeyRef.current,
        progressBag: progressBagRef.current,
        t,
      });
      applyTurn(nextTurn);
    }, pauseMs);

    return () => window.clearTimeout(holdId);
    // Value-only updates must not reset the phrase dwell timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProgress, status, pauseMs, frame.templateKey, frame.kind, t]);

  useEffect(() => {
    if (!isProgress) return;
    if (!progressKeyRef.current || lastWasFunRef.current) return;

    const refreshed = refreshCharacterizationLoadingProgress({
      loaded,
      total: count,
      progressKey: progressKeyRef.current,
      progressBag: progressBagRef.current,
      lastValue: frameRef.current.value,
      lastFunKey: lastFunKeyRef.current,
      funBag: funBagRef.current,
      t,
    });
    if (!refreshed) return;

    lastFactualRef.current = refreshed.lastFactual;
    setFrame((prev) => ({
      ...prev,
      text: refreshed.text,
      value: refreshed.value,
      loaded: refreshed.loaded,
      total: refreshed.total,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, count, isProgress, t]);

  if (!frame.text && !displayed) return null;

  return (
    <div className="dashboard-char-loading">
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {frame.text}
      </p>
      {isProgress ? (
        <ProgressSentence
          text={frame.text}
          value={frame.value}
          reducedMotion={reducedMotion}
        />
      ) : (
        <p className="dashboard-char-loading-text" aria-hidden="true">
          {displayed}
          <span className="dashboard-char-loading-caret">▍</span>
        </p>
      )}
    </div>
  );
};
